import { Divider } from 'antd'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useContext } from 'react'

import { ReactComponent as ExpandFormattingOptionsIcon } from 'assets/icons/expandFormattingOptions32.svg'
import { colorToHex } from 'common/Prose/components/EditorColorPicker/EditorColorPicker'
import { BASIC_TEXT_COLOR_OPTIONS, TEXT_COLOR_OPTIONS } from 'common/Prose/constants/colorOptions'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { Button } from 'components/Button'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { keyboardHeight } from 'utils/virtualKeyboardOverlap'

import { SelfScrollingButton } from '../components/utils/SelfScrollingButton'
import { useMobileToolbar } from '../stores/MobileToolbarStore'

import { removeTextColor, setTextColor } from 'common/Prose/Commands/formatting/textColor'
import Styles from './TextColorSelection.module.scss'

function isColorBtn(colorStr: string) {
  return !/gradient/.test(colorStr)
}

export const TextColorSelectionBar = observer(() => {
  const mobileToolbarStore = useMobileToolbar()
  const view = useEditorView()
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { activeColors } = useContext(AlmanacEditorStateContext)!

  const [defaultColor, ...basicColors] = BASIC_TEXT_COLOR_OPTIONS
  const [, ...allColors] = TEXT_COLOR_OPTIONS
  const activeColorsInHex = activeColors.map((color) => colorToHex(color))
  const noColorSelected = !allColors.some((color) => activeColorsInHex.includes(color))

  return (
    <div className={Styles.bar}>
      <Button
        preventFocusOnPress
        variant="none"
        size="sm"
        key={'expand-options'}
        aria-label="Expand text color options"
        className={cx(Styles.barItem, Styles.extraButton)}
        onClick={() => {
          mobileToolbarStore.toggleDialog()
        }}
        data-testid="mobile-toolbar-text-highlight-expand-options"
      >
        <ExpandFormattingOptionsIcon />
      </Button>

      <Divider type="vertical" className={Styles.divider} />

      <SelfScrollingButton
        isActive={noColorSelected}
        key={'clear-formatting'}
        aria-label="Clear text formatting"
        className={Styles.button}
        onClick={() => {
          removeTextColor(view)
          mobileToolbarStore.closeDialog()
        }}
        data-testid="mobile-toolbar-text-color-clear-formatting"
      >
        <span
          style={{ background: defaultColor }}
          className={cx(Styles.barItem, Styles.colorButton, {
            [Styles.activeItem]: noColorSelected,
            [Styles.colorButton]: isColorBtn(defaultColor),
            [Styles.gradientColor]: !isColorBtn(defaultColor),
          })}
        >
          &nbsp;
        </span>
      </SelfScrollingButton>

      {Object.entries(basicColors).map(([key, color], n) => (
        <SelfScrollingButton
          isActive={activeColorsInHex.includes(color)}
          key={key}
          aria-label={`Set text color to basic option ${n + 1}`}
          className={Styles.button}
          onClick={() => {
            setTextColor(color, view)
            mobileToolbarStore.closeDialog()
          }}
          data-testid={`mobile-toolbar-text-color-${n}`}
        >
          <span
            style={{ background: color }}
            className={cx(Styles.barItem, Styles.colorButton, {
              [Styles.activeItem]: activeColorsInHex.includes(color),
              [Styles.colorButton]: isColorBtn(color),
              [Styles.gradientColor]: !isColorBtn(color),
            })}
          >
            &nbsp;
          </span>
        </SelfScrollingButton>
      ))}
    </div>
  )
})

export const TextColorSelectionDialog = observer(() => {
  const mobileToolbarStore = useMobileToolbar()
  const view = useEditorView()

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { activeColors } = useContext(AlmanacEditorStateContext)!

  const windowHeight = window.visualViewport?.height ?? window.innerHeight
  /* Navbar + Border + MobileToolbar 1st Tier + border + MobileToolbar 2nd Tier */
  const otherContent = 48 + 1 + 48 + 1 + 48
  const availableDialogHeight = windowHeight - keyboardHeight() - otherContent

  return (
    <div className={Styles.dialog} style={{ maxHeight: `${availableDialogHeight}px` }}>
      {Object.entries(TEXT_COLOR_OPTIONS).map(([key, color], n) => (
        <div className={Styles.dialogItem} key={key}>
          <Button
            preventFocusOnPress
            variant="none"
            size="sm"
            aria-label={`Set text color to advanced option ${n + 1}`}
            onClick={() => {
              setTextColor(color, view)
              mobileToolbarStore.closeDialog()
            }}
            className={Styles.button}
            data-testid={`mobile-toolbar-text-color-${n}`}
          >
            <span
              style={{ background: color }}
              className={cx(Styles.colorButton, {
                [Styles.activeItem]: activeColors.includes(color),
                [Styles.colorButton]: isColorBtn(color),
                [Styles.gradientColor]: !isColorBtn(color),
              })}
            >
              &nbsp;
            </span>
          </Button>
        </div>
      ))}
    </div>
  )
})
